import { useState } from "react";
import { Play } from "lucide-react";

interface YouTubeFacadeProps {
  /** Full YouTube embed URL, e.g. https://www.youtube.com/embed/VIDEO_ID */
  embedUrl: string;
  title: string;
  className?: string;
}

function extractVideoId(embedUrl: string): string | null {
  const match = embedUrl.match(/youtube\.com\/embed\/([^?&/]+)/);
  return match ? match[1] : null;
}

/**
 * Lightweight YouTube facade: renders a thumbnail until the user clicks play,
 * then swaps in the real iframe. Avoids loading ~500KB of YouTube player JS
 * on initial page load while preserving the same UX.
 */
export function YouTubeFacade({ embedUrl, title, className = "" }: YouTubeFacadeProps) {
  const [activated, setActivated] = useState(false);
  const videoId = extractVideoId(embedUrl);

  if (!videoId) {
    // Fallback to direct iframe if URL doesn't match expected pattern.
    return (
      <iframe
        src={embedUrl}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className={className}
      />
    );
  }

  if (activated) {
    return (
      <iframe
        // youtube-nocookie.com avoids setting tracking cookies until playback,
        // resolving the Chrome DevTools cookie issue while keeping the same player.
        src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1`}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className={className}
      />
    );
  }

  const thumbnailUrl = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;

  return (
    <button
      type="button"
      onClick={() => setActivated(true)}
      aria-label={`Play video: ${title}`}
      className={`group relative block w-full h-full p-0 border-0 cursor-pointer ${className}`}
      style={{
        backgroundImage: `url(${thumbnailUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <span className="absolute inset-0 bg-foreground/20 group-hover:bg-foreground/30 transition-colors" />
      <span className="absolute inset-0 flex items-center justify-center">
        <span className="flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary/90 group-hover:bg-primary transition-colors shadow-lg">
          <Play className="w-7 h-7 md:w-9 md:h-9 text-primary-foreground fill-current ml-1" />
        </span>
      </span>
    </button>
  );
}
